const SVG_MIME = 'image/svg+xml';

export const isSvgFile = (file) =>
  file?.type === SVG_MIME || /\.svg$/i.test(file?.name || '');

const parseIntrinsicSize = (svgText) => {
  try {
    const doc = new DOMParser().parseFromString(svgText, SVG_MIME);
    const svg = doc.documentElement;
    const width = parseFloat(svg.getAttribute('width'));
    const height = parseFloat(svg.getAttribute('height'));
    if (width > 0 && height > 0) return { width, height };

    const viewBox = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
    if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
      return { width: viewBox[2], height: viewBox[3] };
    }
  } catch {
    // fall through to default
  }
  return null;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load SVG'));
    img.src = src;
  });

/**
 * Rasterize an SVG file to a PNG File using the browser's own SVG renderer.
 * Loading via <img> sandboxes the SVG: scripts never execute and external
 * resources are not fetched, so the resulting PNG is safe for mobile/web.
 */
export const svgFileToPng = async (file, { maxPx = 512 } = {}) => {
  const svgText = await file.text();
  // A data: URL is used rather than a blob: URL because the console CSP allows
  // `data:` in img-src but not `blob:`.
  const dataUrl = `data:${SVG_MIME};charset=utf-8,${encodeURIComponent(svgText)}`;

  const img = await loadImage(dataUrl);
  const intrinsic =
    img.naturalWidth > 0 && img.naturalHeight > 0
      ? { width: img.naturalWidth, height: img.naturalHeight }
      : parseIntrinsicSize(svgText) || { width: maxPx, height: maxPx };

  const scale = maxPx / Math.max(intrinsic.width, intrinsic.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(intrinsic.width * scale));
  canvas.height = Math.max(1, Math.round(intrinsic.height * scale));

  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('SVG conversion failed'))),
      'image/png'
    )
  );

  const name = (file.name || 'logo').replace(/\.svg$/i, '') + '.png';
  return new File([blob], name, { type: 'image/png' });
};
